// Apolo IDE — Learning Loop L0: renderer-side session observer.
//
// Returns an `observe(type, data?)` callback that buffers frontend lifecycle
// events (composer_submit, composer_abort, response_copied, response_dismissed)
// and ships them in batches through `window.apolo.chat.emitEvents`. The bridge
// is intentionally optional — when the IPC channel isn't wired (e.g. browser
// preview, tests without a stub) the observer becomes a silent no-op so the
// UI code path remains identical.

import { useCallback, useEffect, useRef } from 'react';
import { getStoredToken } from '../lib/api.js';
import { getApiBase } from '../lib/apiBase.js';

export const DEBOUNCE_MS = 500;
export const MAX_BUFFER_SIZE = 50;

function getBridge() {
  if (typeof window === 'undefined') return null;
  return window.apolo?.chat?.emitEvents ? window.apolo.chat : null;
}

async function shipEvents(sessionId, events) {
  const bridge = getBridge();
  if (!bridge || !sessionId || events.length === 0) return;
  try {
    await bridge.emitEvents({
      sessionId,
      events,
      // Token + base URL travel with each batch so the Electron main process can
      // forward without needing its own auth state. Both are optional — the
      // handler falls back to its own resolution when absent.
      token: getStoredToken(),
      apiBaseUrl: getApiBase(),
    });
  } catch (err) {
    // Best-effort — log to console but never throw so the UI stays unaffected.
    if (typeof console !== 'undefined') {
      console.warn('[learning:observer] emitEvents failed', err?.message);
    }
  }
}

export function useSessionObserver(sessionId) {
  const queueRef = useRef([]);
  const timeoutRef = useRef(null);
  const sessionIdRef = useRef(sessionId);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (queueRef.current.length === 0) return;
    const events = queueRef.current.splice(0);
    void shipEvents(sessionIdRef.current, events);
  }, []);

  const observe = useCallback(
    (eventType, data = {}) => {
      if (!eventType) return;
      queueRef.current.push({ type: eventType, data, ts: Date.now() });
      if (queueRef.current.length >= MAX_BUFFER_SIZE) {
        flush();
        return;
      }
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(flush, DEBOUNCE_MS);
    },
    [flush],
  );

  useEffect(() => () => flush(), [flush]);

  return observe;
}
